import { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Mail, Clock, MapPin, Send, Check, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeIn = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

export const Contact = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      await axios.post(`${API}/contact`, formData);
      setStatus({ type: 'success', message: t('success', translations.contactPage.form) });
      setFormData({ name: '', email: '', message: '' });
    } catch {
      setStatus({ type: 'error', message: t('error', translations.contactPage.form) });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-[#0f1419]">
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28" data-testid="contact-header">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-15"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1610273561721-d8272de02402?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHw0fHxvaWwlMjByZWZpbmVyeSUyMHBpcGVzJTIwaW5kdXN0cmlhbCUyMHBsYW50JTIwZGFya3xlbnwwfHx8fDE3NzMwNzQ1Mzl8MA&ixlib=rb-4.1.0&q=85)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0f1419] via-[#0f1419]/95 to-[#0f1419]" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-blue-400 text-sm font-mono mb-4 tracking-wider">GET IN TOUCH</p>
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6" data-testid="contact-title">
              {t('title', translations.contactPage)}
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl">
              {t('subtitle', translations.contactPage)}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 lg:py-28 border-t border-gray-800/50" data-testid="contact-content-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
            {/* Info */}
            <motion.div {...fadeIn} className="lg:col-span-2" data-testid="contact-info">
              <p className="text-blue-400 text-sm font-mono mb-4 tracking-wider">CONTACT DETAILS</p>
              <h2 className="text-2xl lg:text-3xl font-bold text-white mb-10">
                {t('title', translations.contactPage.info)}
              </h2>

              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gray-800 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs font-mono tracking-wider mb-1">EMAIL</p>
                    <a 
                      href="mailto:info@natcasgroup.com"
                      className="text-white font-medium text-lg hover:text-blue-400 transition-colors"
                      data-testid="contact-email-link"
                    >
                      info@natcasgroup.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gray-800 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs font-mono tracking-wider mb-1">HOURS</p>
                    <p className="text-white font-medium">{t('hoursValue', translations.contactPage.info)}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gray-800 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs font-mono tracking-wider mb-1">REGION</p>
                    <p className="text-white font-medium">{t('regionValue', translations.contactPage.info)}</p>
                  </div>
                </div>
              </div>

              {/* Map placeholder */}
              <div className="mt-12 aspect-video bg-gray-800/50 border border-gray-700 flex items-center justify-center" data-testid="map-placeholder">
                <div className="text-center">
                  <MapPin className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                  <p className="text-white font-medium">Nordic Region</p>
                  <p className="text-gray-500 text-sm">Industrial Services</p>
                </div>
              </div>
            </motion.div>

            {/* Form */}
            <motion.div {...fadeIn} className="lg:col-span-3 bg-[#0a0d10] p-8 lg:p-12 border border-gray-800" data-testid="contact-form-container">
              <p className="text-blue-400 text-sm font-mono mb-4 tracking-wider">SEND A MESSAGE</p>
              <h2 className="text-2xl lg:text-3xl font-bold text-white mb-10">How can we help?</h2>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <Label htmlFor="name" className="text-gray-500 text-xs font-mono tracking-wider">NAME</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={t('namePlaceholder', translations.contactPage.form)}
                    required
                    className="mt-2 h-14 bg-[#0f1419] border-gray-700 text-white placeholder:text-gray-600 focus:border-blue-500 rounded-none"
                    data-testid="contact-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-gray-500 text-xs font-mono tracking-wider">EMAIL</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t('emailPlaceholder', translations.contactPage.form)}
                    required
                    className="mt-2 h-14 bg-[#0f1419] border-gray-700 text-white placeholder:text-gray-600 focus:border-blue-500 rounded-none"
                    data-testid="contact-email-input"
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="text-gray-500 text-xs font-mono tracking-wider">MESSAGE</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={t('messagePlaceholder', translations.contactPage.form)}
                    required
                    rows={6}
                    className="mt-2 bg-[#0f1419] border-gray-700 text-white placeholder:text-gray-600 focus:border-blue-500 rounded-none resize-none"
                    data-testid="contact-message-input"
                  />
                </div>

                {status.message && (
                  <div className={`flex items-center gap-3 p-4 ${status.type === 'success' ? 'bg-green-900/20 border border-green-800 text-green-400' : 'bg-red-900/20 border border-red-800 text-red-400'}`} data-testid="contact-status-message">
                    {status.type === 'success' ? <Check className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                    <span className="text-sm">{status.message}</span>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-blue-600 hover:bg-blue-500 text-white rounded-none h-14 font-semibold disabled:opacity-50 group"
                  data-testid="contact-submit-button"
                >
                  {isSubmitting ? t('sending', translations.contactPage.form) : (
                    <>
                      {t('submit', translations.contactPage.form)}
                      <Send className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </Button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Bottom */}
      <section className="py-12 border-t border-gray-800/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <p className="text-gray-500 text-sm">
            Prefer email? Reach us at{' '}
            <a href="mailto:info@natcasgroup.com" className="text-blue-400 hover:text-blue-300 transition-colors">
              info@natcasgroup.com
            </a>
          </p>
        </div>
      </section>
    </div>
  );
};

export default Contact;
